import React, { useCallback, useEffect, useReducer, useRef } from 'react'
import { DomainNotificationsProviderContext, domainNotificationsProviderReducer } from './internal'
import { GetNotificationResponse } from '@pushly/models/lib/structs/notifications'
import { parseNotificationResponse } from '../../features/notifications/helpers/notification-helpers'
import { useService } from '@pushly/aqe/lib/hooks'
import { NotificationService } from '../../services'
import { ApiVersion } from '../../enums/api-version.enum'
import { CustomApiResponseErrorHandler } from '../../types/api-call-options-with-custom-error-handler'
import { AbTestNotificationRole } from '@pushly/models/lib/enums/ab-test-notification-role'
import { GetNotificationABTestResponse } from '@pushly/models/lib/structs/notifications/ab-test/get-notification-ab-test-response'
import { NotificationDeliverySpec } from '@pushly/models/lib/structs/notification-delivery-spec'

type NotificationProviderProps = {
    domainId: number
    notificationId?: string | null
    notification?: GetNotificationResponse
    testId?: string | null
    onFetchError?: CustomApiResponseErrorHandler
    children: React.ReactNode
}

export const DomainNotificationsProvider = (props: NotificationProviderProps) => {
    const { domainId, onFetchError, children } = props

    const propsNotificationId: string | undefined | null = 'notificationId' in props ? props.notificationId : undefined
    const propsTestId: number | undefined = props.testId ? Number(props.testId) : undefined

    const notifSvc = useService(NotificationService)

    const [state, dispatch] = useReducer(domainNotificationsProviderReducer, {
        domainId,
        loading: !!props.notificationId,
        notificationId: props.notification?.id?.toString() ?? props.notificationId ?? null,
        notifications: props.notification ? [props.notification] : [],
    })

    // prevents duplicate load effects without triggering re-renders
    const localLoaderInitializedRef = useRef(false)

    const loadRemoteNotification = useCallback(async () => {
        const canLoadRemote = !localLoaderInitializedRef.current || domainId !== state.domainId
        if (!canLoadRemote) return

        if (!propsNotificationId) return

        if (!localLoaderInitializedRef.current) localLoaderInitializedRef.current = true
        if (!state.loading) dispatch({ type: 'loading:set', loading: true })

        const notifications: GetNotificationResponse[] = []

        const notification = await notifSvc.fetchNotificationById(domainId, propsNotificationId, {
            query: { includeSchedules: true },
            cancellationKey: `nl-n${propsNotificationId}`,
            version: ApiVersion.V4,
            errorHandler: onFetchError,
        })

        if (notification) {
            if (!propsTestId) {
                delete notification.ab_test
                notifications.push(notification)
            } else {
                const {
                    notifications: _notifications,
                    schedules,
                    ...test
                } = (await notifSvc.fetchTestById(domainId, propsTestId, undefined, `nl-t${propsTestId}`))!

                const testNotifications = _notifications

                testNotifications
                    .map((n) => parseNotificationResponse(n))
                    .filter((notif) => {
                        // Exclude rollout/winner notification from variants when duplicating
                        return notif.ab_test?.role !== AbTestNotificationRole.ROLLOUT
                    })
                    .forEach((notif) => {
                        notif.audience = notification?.audience! ?? {}

                        // Enrich ab_test with test-level data (domain_id, test_types, etc.)
                        // Object.assign mutates the existing instance in place, preserving
                        // both the original ab_test fields and the additional test fields
                        // that downstream consumers access (e.g. test_types, delivery_type).
                        if (notif.ab_test) {
                            Object.assign(notif.ab_test, test)
                        } else {
                            notif.ab_test = Object.assign(new GetNotificationABTestResponse(), test)
                        }

                        const schedule = schedules.find((s) => {
                            return s.notification_id.toString() === notif.id.toString()
                        })

                        if (schedule) {
                            notif.status = schedule.status ?? notif.status
                            // Merge schedule delivery_spec with notification delivery_spec,
                            // producing a proper NotificationDeliverySpec instance
                            notif.delivery_spec = NotificationDeliverySpec.from({
                                ...schedule.delivery_spec,
                                ...notif.delivery_spec,
                            })
                        }

                        notifications.push(notif)
                    })
            }
        }

        if (notifications.length) {
            dispatch({ type: 'notifications:set', notifications })
        }

        dispatch({ type: 'loading:set', loading: false })
    }, [domainId, propsNotificationId, propsTestId])

    useEffect(() => {
        loadRemoteNotification()
    }, [loadRemoteNotification])

    return (
        <DomainNotificationsProviderContext.Provider value={{ state, dispatch }}>
            {children}
        </DomainNotificationsProviderContext.Provider>
    )
}
